/**
 * Episode Tags Module
 * Handles tag management for episode view
 */

import { API } from '../core/api.js';
import { showNotification } from '../components/notifications.js';
import { escapeHtml } from '../core/utils.js';

// Cache for all tags (for autocomplete)
let allTagsCache = null;
let tagsCacheTime = 0;
const TAGS_CACHE_TTL = 60000; // 1 minute

/**
 * Add a tag to the current episode
 */
export async function addTag(tag, currentEpisodeId) {
    if (!tag || !window.currentEpisodeData) return;

    const normalizedTag = tag.trim().toLowerCase();
    if (!normalizedTag) return;

    const tags = [...(window.currentEpisodeData.tags || [])];
    if (tags.includes(normalizedTag)) {
        showNotification('Tag already exists', 'warning');
        return;
    }

    tags.push(normalizedTag);

    try {
        await API.put(`/api/episodes/${currentEpisodeId}/tags`, { tags });
        window.currentEpisodeData.tags = tags;
        refreshTagChips();
        allTagsCache = null;
        showNotification('Tag added', 'success');
    } catch (error) {
        console.error('Error adding tag:', error);
        showNotification('Failed to add tag', 'error');
    }
}

/**
 * Remove a tag from the current episode
 */
export async function removeTag(tag, currentEpisodeId) {
    if (!window.currentEpisodeData) return;

    const tags = (window.currentEpisodeData.tags || []).filter(t => t !== tag);

    try {
        await API.put(`/api/episodes/${currentEpisodeId}/tags`, { tags });
        window.currentEpisodeData.tags = tags;
        refreshTagChips();
        showNotification('Tag removed', 'success');
    } catch (error) {
        console.error('Error removing tag:', error);
        showNotification('Failed to remove tag', 'error');
    }
}

/**
 * Refresh the tag chips display
 */
export function refreshTagChips() {
    const container = document.getElementById('episode-tag-chips');
    if (!container || !window.currentEpisodeData) return;

    const tags = window.currentEpisodeData.tags || [];
    container.textContent = '';
    
    tags.forEach(tag => {
        const span = document.createElement('span');
        span.className = 'tag-chip';
        span.dataset.tag = tag;
        span.textContent = tag;
        
        const btn = document.createElement('button');
        btn.className = 'tag-remove';
        btn.title = 'Remove';
        btn.textContent = '×';
        btn.onclick = () => removeTag(tag, window.currentEpisodeId);
        
        span.appendChild(btn);
        container.appendChild(span);
    });
}

/**
 * Show tag suggestions based on input
 */
export async function showTagSuggestions(query) {
    const suggestionsEl = document.getElementById('tag-suggestions');
    if (!suggestionsEl) return;

    if (!query || query.length < 1) {
        hideSuggestions();
        return;
    }

    const now = Date.now();
    if (!allTagsCache || (now - tagsCacheTime) > TAGS_CACHE_TTL) {
        try {
            allTagsCache = await API.get('/api/tags');
            tagsCacheTime = now;
        } catch (error) {
            console.error('Error fetching tags:', error);
            return;
        }
    }

    const currentTags = window.currentEpisodeData?.tags || [];
    const queryLower = query.toLowerCase();

    const matches = allTagsCache.filter(t =>
        t.toLowerCase().includes(queryLower) &&
        !currentTags.includes(t)
    ).slice(0, 8);

    if (matches.length === 0) {
        hideSuggestions();
        return;
    }

    renderSuggestions(matches);
}

/**
 * Render tag suggestions dropdown
 */
function renderSuggestions(matches) {
    const suggestionsEl = document.getElementById('tag-suggestions');
    if (!suggestionsEl) return;

    suggestionsEl.textContent = '';
    matches.forEach(tag => {
        const div = document.createElement('div');
        div.className = 'tag-suggestion';
        div.textContent = tag;
        div.onclick = () => selectTagSuggestion(tag);
        suggestionsEl.appendChild(div);
    });
    suggestionsEl.classList.add('visible');
}

/**
 * Hide tag suggestions dropdown
 */
export function hideSuggestions() {
    const suggestionsEl = document.getElementById('tag-suggestions');
    if (suggestionsEl) {
        suggestionsEl.classList.remove('visible');
        suggestionsEl.textContent = '';
    }
}

/**
 * Select a tag from suggestions
 */
export function selectTagSuggestion(tag) {
    addTag(tag, window.currentEpisodeId);
    const input = document.getElementById('tag-input');
    if (input) input.value = '';
    hideSuggestions();
}

/**
 * Handle keydown in tag input
 */
export function handleTagInput(e) {
    const suggestionsEl = document.getElementById('tag-suggestions');
    const isVisible = suggestionsEl?.classList.contains('visible');

    if (e.key === 'Enter') {
        e.preventDefault();
        const tag = e.target.value.trim();
        if (tag) {
            addTag(tag, window.currentEpisodeId);
            e.target.value = '';
            hideSuggestions();
        }
    } else if (e.key === 'Escape') {
        hideSuggestions();
        e.target.blur();
    } else if (e.key === 'ArrowDown' && isVisible) {
        const firstSuggestion = suggestionsEl.querySelector('.tag-suggestion');
        if (firstSuggestion) {
            e.preventDefault();
            firstSuggestion.focus();
        }
    }
}
